import { init, reactRouterV7BrowserTracingIntegration, setTag, } from '@sentry/react';
import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType, } from 'react-router-dom';
import { getClientData } from '@lucidlink/uicore';
export { wrapCreateBrowserRouterV7 } from '@sentry/react';
// What we send to Sentry's performance product:
// One `pageload` transaction per app load and one `navigation` transaction per
// route change. Each transaction is opened by the BrowserTracing integration,
// stays open until ~1s of network/UI idle (capped by `finalTimeout`), and
// collects every fetch, resource load, and browser lifecycle event that fires
// inside that window as a child span. A typical pageload produces ~30 spans:
//   - `http.client`         our React Query bootstrap fetches
//   - `browser.*`           PerformanceTiming replays (DNS/connect/load/etc.)
//   - `resource.*`          PerformanceResourceTiming (scripts/images/css)
//   - `ui.long-animation-frame`  frames blocked >50ms
//   - `mark` / `measure`    React DevTools and app `performance.mark()` calls
//   - `paint`               first paint
// Web Vitals (LCP, FCP, CLS, TTFB, INP) are attached to the transaction's
// `measurements` object — NOT to child spans — so spans can be filtered
// without losing vitals. `beforeSendTransaction` below scrubs IDs out of URLs
// in every place the SDK plants them.
const SENTRY_DSN = process.env.SENTRY_DSN;
const APP_VERSION = process.env.APP_VERSION;
const SENTRY_ENVIRONMENT = process.env.SENTRY_ENVIRONMENT;
const SENTRY_TRACES_SAMPLE_RATE = Number(process.env.SENTRY_TRACES_SAMPLE_RATE);
const SHOULD_ENABLE_TRACING = SENTRY_TRACES_SAMPLE_RATE > 0;
const UUID_RE = /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i;
const LONG_HEX_RE = /^[\da-f]{16,}$/i;
const OPAQUE_ID_RE = /^[\w-]+$/;
const NUMERIC_ID_RE = /^\d{6,}$/;
// Replace any URL path segment that looks like an opaque ID so transactions and
// URLs don't carry workspace/filespace/share IDs into Sentry. The router-level
// instrumentation handles transaction names for WebClient (BrowserRouter); this
// is the only line of defense for DesktopClient (HashRouter has no Sentry
// wrapper in @sentry/react@10.x) and a safety net for both.
const looksLikeId = (segment) => {
    if (UUID_RE.test(segment)) {
        return true;
    }
    if (LONG_HEX_RE.test(segment)) {
        return true;
    }
    if (segment.length >= 12 &&
        /\d/.test(segment) &&
        OPAQUE_ID_RE.test(segment)) {
        return true;
    }
    if (NUMERIC_ID_RE.test(segment)) {
        return true;
    }
    return false;
};
const parameterizePath = (path) => path
    .split('/')
    .map((seg) => (looksLikeId(seg) ? ':id' : seg))
    .join('/');
const sanitizeUrl = (rawUrl) => {
    const withoutQuery = rawUrl.split('?')[0] ?? '';
    // Electron renders from a file:// URL whose path leaks the install location
    // (and on macOS the home directory / username). Collapse it to a stable
    // placeholder and keep only the in-app hash route.
    if (withoutQuery.startsWith('file://')) {
        const hashIndex = withoutQuery.indexOf('#');
        return hashIndex >= 0
            ? `app://desktop${parameterizePath(withoutQuery.slice(hashIndex + 1))}`
            : 'app://desktop';
    }
    return parameterizePath(withoutQuery);
};
// Spans carry the raw URL in several places: http.client uses `data.url` /
// `data['http.url']` and a `"METHOD url"` description; `browser.*` event
// spans put a bare URL in `description`; `ui.long-animation-frame` puts the
// pageload URL in `data['browser.script.invoker']` and `data['code.filepath']`.
// Replace IDs / drop query strings everywhere so workspace/filespace IDs don't
// leak and don't blow up Sentry's per-attribute cardinality.
const URL_DATA_KEYS = [
    'url',
    'http.url',
    'browser.script.invoker',
    'code.filepath',
];
const sanitizeSpanData = (data) => {
    for (const key of URL_DATA_KEYS) {
        const value = data[key];
        if (typeof value === 'string') {
            data[key] = sanitizeUrl(value);
        }
    }
    delete data['http.query'];
};
const sanitizeSpanDescription = (description) => {
    const spaceIndex = description.indexOf(' ');
    if (spaceIndex < 0) {
        // `browser.*` event spans use a bare URL as the description.
        return /^(?:https?:|file:|app:)/.test(description)
            ? sanitizeUrl(description)
            : description;
    }
    const method = description.slice(0, spaceIndex);
    const url = description.slice(spaceIndex + 1);
    return `${method} ${sanitizeUrl(url)}`;
};
// Screen resolution for Sentry usage stats; mobile devices are marked `mobile`.
const getScreenTag = () => {
    if (getClientData().isMobile) {
        return 'mobile';
    }
    return `${Math.round(window.screen.width)}x${Math.round(window.screen.height)}`;
};
export const initSentry = ({ release } = {}) => {
    if (SENTRY_DSN && SENTRY_ENVIRONMENT) {
        const options = {
            beforeSendTransaction(event) {
                if (event.request?.url) {
                    event.request.url = sanitizeUrl(event.request.url);
                }
                const headers = event.request?.headers;
                if (headers?.Referer) {
                    headers.Referer = sanitizeUrl(headers.Referer);
                }
                // When the router wrap doesn't run (HashRouter on DesktopClient)
                // the SDK names transactions from the raw URL — parameterize it
                // here so cardinality stays bounded.
                if (event.transaction &&
                    event.transaction_info?.source === 'url') {
                    event.transaction = sanitizeUrl(event.transaction);
                }
                if (event.spans) {
                    for (const span of event.spans) {
                        if (span.data) {
                            sanitizeSpanData(span.data);
                        }
                        if (typeof span.description === 'string') {
                            span.description = sanitizeSpanDescription(span.description);
                        }
                    }
                }
                // Drop breadcrumbs from transactions: they restate each fetch
                // span (with a `__span` pointer) and add no analysis value to
                // performance events. Kept on errors via the default path.
                delete event.breadcrumbs;
                return event;
            },
            dsn: SENTRY_DSN,
            environment: SENTRY_ENVIRONMENT,
            integrations: SHOULD_ENABLE_TRACING
                ? [
                    reactRouterV7BrowserTracingIntegration({
                        createRoutesFromChildren,
                        matchRoutes,
                        useEffect,
                        useLocation,
                        useNavigationType,
                    }),
                ]
                : [],
            ...(release !== null && { release: `${release ?? APP_VERSION}` }),
            sendDefaultPii: false,
            ...(SHOULD_ENABLE_TRACING && {
                tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
            }),
        };
        init(options);
        setTag('screen', getScreenTag());
    }
};
